/**
 * 週次MTG のボード（FigJam の枠を写した見た目）の純粋な組み立て。区分をボードの置き場に振り分け、
 * 付箋の色を投稿者から決める。
 *
 * 置き場は区分 ID で決める（ラベルは書き換えられるので見ない）。既定の区分以外はボードの下に並べる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG のボードで区分を左（良かったこと）・右（聞いて欲しい・変えたい）・中央（AI Sharing）に振り分け、付箋の色を投稿者ごとに決める
 * @graph-connects none
 */

import type { CategoryColumn } from './team-meetings.js';

/** ボードの左半分（にっこり側）に上から並べる区分。 @graph-connects none */
const LEFT_IDS = ['thanks', 'brag', 'keep'] as const;

/** ボードの右半分（しょんぼり側）に上から並べる区分。 @graph-connects none */
const RIGHT_IDS = ['listen', 'change'] as const;

/** ボードの中央の柱に置く区分。 @graph-connects none */
const CENTER_ID = 'ai_sharing';

/** ボードの置き場ごとの区分。 @graph-connects none */
export interface BoardLayout {
  /** 左半分（上から）。シリーズに無い区分は詰める */
  left: CategoryColumn[];
  /** 右半分（上から） */
  right: CategoryColumn[];
  /** 中央の柱。シリーズに無ければ `null` */
  center: CategoryColumn | null;
  /** ボードの枠に無い区分（独自の区分・消された区分の投稿）。列の並び順のまま */
  extra: CategoryColumn[];
}

/**
 * 区分の列をボードの置き場に振り分ける。どの置き場にも当たらない列は `extra` に回す（黙って消さない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Place meeting category columns on the left, right and center of the board, keeping unknown categories below it
 * @graph-connects none
 */
export function toBoardLayout(columns: readonly CategoryColumn[]): BoardLayout {
  const byId = (id: string) => columns.find((c) => c.category.id === id);
  const pick = (ids: readonly string[]) =>
    ids.map(byId).filter((c): c is CategoryColumn => c !== undefined);
  const placed = new Set<string>([...LEFT_IDS, ...RIGHT_IDS, CENTER_ID]);
  return {
    left: pick(LEFT_IDS),
    right: pick(RIGHT_IDS),
    center: byId(CENTER_ID) ?? null,
    extra: columns.filter((c) => !placed.has(c.category.id)),
  };
}

/** 付箋の色（FigJam の付箋の色）。 @graph-connects none */
export const STICKY_COLORS = [
  'blue',
  'green',
  'yellow',
  'orange',
  'red',
  'pink',
  'violet',
  'teal',
] as const;

/** 付箋の色の名前。 @graph-connects none */
export type StickyColor = (typeof STICKY_COLORS)[number];

/**
 * 投稿者の付箋の色。同じ人は毎回同じ色になる（ID の文字コードの和で決める）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Pick the same sticky note color for the same author every time
 * @graph-connects none
 */
export function stickyColorOf(authorId: string): StickyColor {
  let sum = 0;
  for (let i = 0; i < authorId.length; i += 1) sum += authorId.charCodeAt(i);
  return STICKY_COLORS[sum % STICKY_COLORS.length];
}

/**
 * 付箋に出す本文の頭（長い投稿は付箋では切り、全文は開いて読む）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Shorten a long post body for its sticky note, leaving the full text to the detail view
 * @graph-connects none
 */
export function stickyExcerpt(body: string, limit = 140): string {
  const trimmed = body.trim();
  return trimmed.length <= limit ? trimmed : `${trimmed.slice(0, limit).trimEnd()}…`;
}

/** ボードの拡大率の段（ボタンで 1 段ずつ動かす）。 @graph-connects none */
export const BOARD_ZOOM_STEPS = [
  0.25, 0.33, 0.5, 0.67, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2,
] as const;

/** 拡大率の下限・上限。 @graph-connects none */
const ZOOM_MIN = BOARD_ZOOM_STEPS[0];
/** @graph-connects none */
const ZOOM_MAX = BOARD_ZOOM_STEPS[BOARD_ZOOM_STEPS.length - 1];

/**
 * 拡大率を下限・上限に収め、小数 2 桁に丸める。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Keep the weekly meeting board zoom within its bounds
 * @graph-connects none
 */
export function clampZoom(zoom: number): number {
  return Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom)) * 100) / 100;
}

/**
 * ボタンで 1 段拡大・縮小した拡大率（段の間にいるときは、その向きの次の段へ）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Move the weekly meeting board zoom one step in or out
 * @graph-connects none
 */
export function stepZoom(zoom: number, direction: 'in' | 'out'): number {
  const next =
    direction === 'in'
      ? BOARD_ZOOM_STEPS.find((s) => s > zoom + 0.001)
      : [...BOARD_ZOOM_STEPS].reverse().find((s) => s < zoom - 0.001);
  return next ?? clampZoom(zoom);
}

/**
 * ホイール（トラックパッドのピンチ）の量で変えた拡大率。下に回すと縮む。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Zoom the weekly meeting board smoothly with the wheel or a trackpad pinch
 * @graph-connects none
 */
export function wheelZoom(zoom: number, deltaY: number): number {
  return clampZoom(zoom * Math.exp(-deltaY * 0.01));
}

/** 大きさ（px）。 @graph-connects none */
interface Size {
  width: number;
  height: number;
}

/**
 * ボードを表示枠に収める拡大率。`width` は横幅だけ、`all` は全体（縦も）を収める。等倍より大きくはしない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Pick the zoom that fits the whole weekly meeting board, or its width, into the visible area
 * @graph-connects none
 */
export function fitZoom(viewport: Size, content: Size, mode: 'width' | 'all'): number {
  if (content.width <= 0 || content.height <= 0) return 1;
  const byWidth = viewport.width / content.width;
  const ratio = mode === 'width' ? byWidth : Math.min(byWidth, viewport.height / content.height);
  return clampZoom(Math.min(1, Math.floor(ratio * 100) / 100));
}

/** ボードの左上が表示枠のどこにあるか（px。左・上にはみ出すと負になる）。 @graph-connects none */
export interface BoardPan {
  x: number;
  y: number;
}

/**
 * 拡大率を変えたあとのボードの位置（1 軸ぶん）。指している点（表示枠の中の `at`）が同じ場所に残るようにする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Keep the point under the cursor in place while zooming the weekly meeting board
 * @graph-connects none
 */
export function anchoredPan(pan: number, at: number, zoom: { from: number; to: number }): number {
  return at - ((at - pan) * zoom.to) / zoom.from;
}

/** 動かしてもボードを見失わないよう、表示枠に残すボードの幅（px）。 @graph-connects none */
const PAN_KEEP_VISIBLE = 120;

/**
 * ボードの位置を、表示枠にボードが少し（{@link PAN_KEEP_VISIBLE}）残る範囲に収める。左・上へは負の位置まで
 * 動かせる（FigJam のキャンバスと同じく、ボードの外側にも余白がある）。大きさが測れない間は絞らない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Let the weekly meeting board move freely in every direction, including past its top-left corner, without losing it off screen
 * @graph-connects none
 */
export function clampPan(pan: BoardPan, viewport: Size, content: Size): BoardPan {
  const clamp = (value: number, view: number, size: number) => {
    if (view <= 0 || size <= 0) return value;
    const keep = Math.min(PAN_KEEP_VISIBLE, view, size);
    return Math.min(view - keep, Math.max(keep - size, value));
  };
  return {
    x: clamp(pan.x, viewport.width, content.width),
    y: clamp(pan.y, viewport.height, content.height),
  };
}

/**
 * 収めたときのボードの位置。`all` は表示枠の中央に置き、`width` は横だけ中央に寄せて上端に揃える。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Place the fitted weekly meeting board in the middle of the visible area
 * @graph-connects none
 */
export function fitPan(viewport: Size, content: Size, mode: 'width' | 'all'): BoardPan {
  const center = (view: number, size: number) => Math.max(0, Math.round((view - size) / 2));
  return {
    x: center(viewport.width, content.width),
    y: mode === 'all' ? center(viewport.height, content.height) : 0,
  };
}
